import React, { FC, useMemo, useReducer, useState } from 'react';
import { useCommonStyles } from '../../../../../../common/styles/common-styles';
import { NEWS_BASE_PATH, NEWS_CATEGORY_PATH } from '../../../../constants';
import { makeStyles } from 'tss-react/mui';
import { Button, TextField, Dialog, LinearProgress, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import { useErrorHandler, ErrorFieldType, fileUpload } from 'src/app/common/utils';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import {
  NewsCategoryEditItem,
  NewsCategoryEmailItem,
  NewsCategoryFormMode,
  NewsCategoryIconItem, //PHKL Custom
  NewsCategoryItem,
  NewsTitleItem,
} from '../../../../types/news-type';
import { MuiColorInput } from 'mui-color-input';
import { modifyNewsCategory } from '../../../../network/newsCrud';
import { appendAlertItem, AlertType } from '@pruforce/common-adminweb-sdk';
import { getAvailableServerLocale } from 'src/app/i18n';
import { useStyles } from './style';
import { get, isEmpty, isNil } from 'lodash';
import { useCategoryForm } from './hook/category-form.hook';
import { ModifyLocaleFieldAction } from 'src/app/modules/Config/pages/agent-sub-segment/sub-segment-detail/sub-segment-form/util';
import { SegmentNameItem } from 'src/app/modules/Config/types/agent-segment-type';
import { SubmitAlertComponent } from 'src/app/modules/Config/pages/AgentSegment/View/components/submit-alert/submit-alert.component';
//PHKL Custom -s
import { createBlob, getBlob } from 'src/app/common/network';
//PHKL Custom -e

type NewsCategoryDetailFormProps = {
  formMode: NewsCategoryFormMode;
  newsCategoryDetail?: NewsCategoryItem;
  code: string;
  onRouteTo: (route: string) => void;
};

export type NewsCategoryFormState = {
  color?: string;
  maxNoOfHeadlines: string;
  maxArchiveDays: string;
  icon?: NewsCategoryIconItem; //PHKL Custom
  iconUrl?: string; //PHKL Custom
  active?: boolean;
  name?: SegmentNameItem;
  emails?: NewsCategoryEmailItem[]; // old field, not use anymore?
  // dynamic field: i18n category-name
  // [key: string]: string | undefined | boolean;
};

const initialState: NewsCategoryFormState = {
  color: '#caabbb',
  maxNoOfHeadlines: '1',
  maxArchiveDays: '90',
  active: true, // default true
  name: {},
  //PHKL Custom -s
  icon: {
    blobId: '',
    fileName: '',
    url: '',
  },
  //PHKL Custom -e
};

type ModifyFieldAction = {
  type: 'MODIFY_FIELD';
  payload: {
    field: keyof NewsCategoryFormState;
    value: any;
  };
};

type AddEmailsRowAction = {
  type: 'ADD_EMAIL_ROW';
};

type ModifyEmailsRowAction = {
  type: 'MODIFY_EMAIL_ROW';
  payload: {
    index: number;
    field: keyof NewsCategoryEmailItem;
    value: string;
  };
};

type DeleteEmailsRowAction = {
  type: 'DELETE_EMAIL_ROW';
  payload: {
    index: number;
  };
};
//PHKL Custom -s
type UploadIconImageAction = {
  type: 'UPLOAD_ICON_IMAGE';
  payload: {
    blobId: string;
    fileName: string;
    url: string;
  };
};
//PHKL Custom -e

type NewsCategoryFormAction =
  | ModifyFieldAction
  | ModifyLocaleFieldAction
  | ModifyEmailsRowAction
  | AddEmailsRowAction
  | DeleteEmailsRowAction
  | UploadIconImageAction /*PHKL Custom*/;

const newsCategoryReducer = (state: NewsCategoryFormState, action: NewsCategoryFormAction) => {
  switch (action.type) {
    // i18n field, example: { name: { "enUs": "xx","zhHk": "yy"} }
    case 'MODIFY_LOCALE_FIELD':
      return {
        ...state,
        [action.payload.field]: {
          ...state[action.payload.field],
          [action.payload.locale]: action.payload.value,
        },
      };
    case 'MODIFY_FIELD':
      return {
        ...state,
        [action.payload.field]: action.payload.value,
      };
    case 'ADD_EMAIL_ROW':
      return {
        ...state,
        emails: state.emails ? [...state.emails, { name: '', address: '' }] : [{ name: '', address: '' }],
      };
    case 'MODIFY_EMAIL_ROW':
      if (state.emails && state.emails[action.payload.index]) {
        let item = state.emails[action.payload.index];
        item[action.payload.field] = action.payload.value;
      }
      return { ...state };
    case 'DELETE_EMAIL_ROW':
      if (state.emails && state.emails[action.payload.index]) {
        let emailArr = state.emails;
        emailArr.splice(action.payload.index, 1);
      }
      return { ...state };
    //PHKL Custom -s
    case 'UPLOAD_ICON_IMAGE':
      return {
        ...state,
        icon: {
          blobId: action.payload.blobId,
          fileName: action.payload.fileName,
          url: action.payload.url,
        },
      };
    //PHKL Custom -e
    default:
      return initialState;
  }
};

const detailToStateConvertor = (newsCategoryDetail?: NewsCategoryItem): NewsCategoryFormState => {
  return newsCategoryDetail
    ? {
        name: newsCategoryDetail.name || {},
        active: newsCategoryDetail.active,
        // isNil(newsCategoryDetail.active) || isEmpty(newsCategoryDetail.active)
        //   ? true
        //   : Boolean(newsCategoryDetail.active),
        color: newsCategoryDetail.color,
        emails: newsCategoryDetail.emails,
        icon: newsCategoryDetail.icon, //PHKL Custom
        iconUrl: newsCategoryDetail.icon?.url, //PHKL Custom
        maxNoOfHeadlines:
          typeof newsCategoryDetail.maxNoOfHeadlines === 'number' ? newsCategoryDetail.maxNoOfHeadlines.toString() : '',
        maxArchiveDays:
          typeof newsCategoryDetail.maxNoOfHeadlines === 'number' ? newsCategoryDetail.maxArchiveDays.toString() : '',
      }
    : initialState;
};

export type NewsCategoryErrorState = {
  mandatory: {
    iconUrl: boolean; //PHKL Custom
    email: boolean;
    color: boolean;
    maxNoOfHeadlines: boolean;
    maxArchiveDays: boolean;
  };
  immediate: {
    emailFormat: boolean;
  };
};

function validatePositiveInteger(input: string) {
  const trimmedInput = String(input).trim();
  const regex = /^[1-9]\d*$/;
  const isValid = regex.test(trimmedInput);

  return isValid;
}

const NewsCategoryDetailForm: FC<NewsCategoryDetailFormProps> = ({ formMode, newsCategoryDetail, code, onRouteTo }) => {
  const isEditMode = formMode === NewsCategoryFormMode.EDIT;
  const availableLocales = getAvailableServerLocale();
  const dispatch = useDispatch();
  const { classes: commonClasses } = useCommonStyles();
  const { classes } = useStyles();
  const intl = useIntl();
  const Translation = (id: string, variable?: Record<string, string>) => intl.formatMessage({ id }, variable);

  //PHKL Custom -s
  const TranslationWithVariable = (key: string, count: number | string) =>
    intl.formatMessage({ id: key }, { num: count });

  const [uploadIconProgress, setUploadIconProgress] = useState<number>();
  const [isChangeIcon, setIsChangeIcon] = useState<boolean>(false);
  //PHKL Custom -e

  const [errorDialog, setErrorDialog] = useState({
    show: false,
    message: '',
  });
  const initDetail = useMemo(() => detailToStateConvertor(newsCategoryDetail), [newsCategoryDetail]);
  const [formState, formDispatch] = useReducer(newsCategoryReducer, initDetail);

  const { errorState, onSubmitErrorValidator, onDismissErrorHandler } = useErrorHandler(formState, [
    //PHKL Custom -s
    {
      name: 'iconUrl',
      fieldType: ErrorFieldType.MANDATORY,
    },
    //PHKL Custom -e
    {
      name: 'color',
      fieldType: ErrorFieldType.MANDATORY,
    },
    ...availableLocales.map((locale) => ({
      name: `name${locale}`,
      fieldType: ErrorFieldType.MANDATORY,
      condition: () => {
        let flag = false;
        if (!formState.name || !formState.name[locale]) {
          flag = true;
        }
        return flag;
      },
    })),
    {
      name: 'color',
      fieldType: ErrorFieldType.MANDATORY,
    },
    {
      name: 'maxNoOfHeadlines',
      fieldType: ErrorFieldType.MANDATORY,
      condition: () => {
        return !validatePositiveInteger(formState.maxNoOfHeadlines);
      },
    },
    {
      name: 'maxArchiveDays',
      fieldType: ErrorFieldType.MANDATORY,
      condition: () => {
        return !validatePositiveInteger(formState.maxArchiveDays);
      },
    },
  ]);
  const { onOk, onCancel, onSubmitForm, confirmMsg } = useCategoryForm({ isEditMode, onRouteTo, id: code });
  const onSubmit = async () => {
    let { hasError } = onSubmitErrorValidator();
    if (!hasError) {
      // PHKL Custom: -s
      onSubmitForm(
        {
          icon: isChangeIcon ? formState.icon : undefined, //PHKL Custom,
          ...formState,
        },
        initDetail.active,
      );
      // PHKL Custom: -e
    }
  };

  //PHKL Custom -s
  const handleIconFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const fileSize = file.size / 1024;
      e.target.value = '';
      const errMsgImage = TranslationWithVariable('global.max.file.size.allow', '') || '';
      if (fileSize > 300) {
        dispatch(
          appendAlertItem([
            {
              severity: AlertType.ERROR,
              title: '',
              content: errMsgImage,
            },
          ]),
        );
        return;
      }

      try {
        const createBlobRes = await createBlob(
          { mimeType: file.type, accessLevel: 'anonymous', module: 'prunews' },
          dispatch,
        );
        await fileUpload(createBlobRes.url, file, setUploadIconProgress);
        const blobDetail = await getBlob({ resourceIds: createBlobRes.blobId }, dispatch);
        const result = blobDetail[0];
        if (result) {
          setIsChangeIcon(true);
          formDispatch({
            type: 'UPLOAD_ICON_IMAGE',
            payload: {
              blobId: result.blobId,
              fileName: file.name,
              url: result.url,
            },
          });
          onDismissErrorHandler('iconUrl', result.url);
          formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'iconUrl', value: result.url } });
        }

        dispatch(
          appendAlertItem([
            {
              severity: AlertType.SUCCESS,
              title: 'Success',
              content: Translation('newsCategory.success.uploadIcon', {
                name: file.name,
              }),
            },
          ]),
        );
      } catch (err) {
      } finally {
        setUploadIconProgress(undefined);
      }
    }
  };

  const removeIconFile = () => {
    setIsChangeIcon(true);
    formDispatch({
      type: 'UPLOAD_ICON_IMAGE',
      payload: {
        blobId: '',
        fileName: '',
        url: '',
      },
    });
    formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'iconUrl', value: '' } });

    dispatch(
      appendAlertItem([
        {
          severity: AlertType.SUCCESS,
          title: 'Success',
          content: `Remove icon file successfully`,
        },
      ]),
    );
  };
  //PHKL Custom -e

  return (
    <>
      <div className={classes.container}>
        <div className={classes.headerContainer}>
          <div className={classes.rowContainer}>
            <div className={commonClasses.header}>
              {isEditMode ? `${Translation('newsCategory.editMain')}` : Translation('component.formTitle.add-category')}
            </div>
          </div>
          <Button
            variant="contained"
            color="inherit"
            onClick={() => {
              onRouteTo(`${NEWS_BASE_PATH}${NEWS_CATEGORY_PATH}`);
            }}
          >
            {Translation('app.button.back')}
          </Button>
        </div>

        {availableLocales.map((locale) => (
          <div key={locale} className={classes.rowContainer}>
            <div className={classes.fieldContainer}>
              <span className={classes.field}>
                {Translation(`newsCategory.common.name.${locale}`)}
                <span className={classes.mandatory}>*</span> :
              </span>
            </div>
            <div className={classes.flexGrow}>
              <TextField
                margin="dense"
                variant="outlined"
                fullWidth
                inputProps={{ maxLength: 30 }}
                error={errorState.mandatory[`name${locale}`]}
                helperText={errorState.mandatory[`name${locale}`] && Translation('field_mandatory_helper_label')}
                value={get(formState, `name.${locale}`) || ''}
                onChange={(e) => {
                  onDismissErrorHandler(`name${locale}`, e.target.value);
                  formDispatch({
                    type: 'MODIFY_LOCALE_FIELD',
                    payload: { field: 'name', locale, value: e.target.value },
                    // payload: { field: `title${locale}`, value: e.target.value },
                  });
                }}
              />
            </div>
          </div>
        ))}
        {/* //PHKL Custom -s */}
        <div className={classes.rowContainer} style={{ marginTop: 15 }}>
          <div className={classes.fieldContainer} style={{ alignSelf: 'flex-start', paddingTop: '8px' }}>
            <span className={classes.field}>
              {Translation('newsCategory.common.icon')}
              <span className={classes.mandatory}>*</span> :
            </span>
          </div>
          <div style={{ flexGrow: 1 }}>
            <input
              id="upload-banner"
              hidden
              type="file"
              accept="image/jpg, image/jpeg, image/png"
              onChange={handleIconFile}
            />
            <div className={classes.rowContainer}>
              <Button
                style={{ border: errorState.mandatory.iconUrl ? `1px solid #f018a6` : `0px` }}
                variant="contained"
                color="secondary"
                onClick={() => document.getElementById('upload-banner')!.click()}
              >
                {Translation('newsCategory.common.chooseFile')}
              </Button>

              {formState.icon?.url ? (
                <Button
                  style={{ marginLeft: 20 }}
                  variant="contained"
                  color="secondary"
                  onClick={() => removeIconFile()}
                >
                  {Translation('newsCategory.common.removeFile')}
                </Button>
              ) : null}
            </div>
            {errorState.mandatory.iconUrl && (
              <span className={classes.errorText}>{Translation('field_mandatory_helper_label')}</span>
            )}
            <div>{Translation('newsCategory.common.iconTips')}</div>

            {!!uploadIconProgress && (
              <LinearProgress style={{ marginTop: 10 }} variant="determinate" value={uploadIconProgress} />
            )}
          </div>
        </div>

        <div className={classes.rowContainer} style={{ marginTop: 15 }}>
          <div className={classes.fieldContainer}></div>
          <div style={{ flexGrow: 1 }}>
            <div className={classes.rowContainer}>
              <div>{formState.icon?.fileName ? formState.icon.fileName : ''}</div>
            </div>
            <div className={classes.rowContainer}>
              {formState.icon?.url && (
                <div>
                  <img style={{ width: 'auto', height: '120px' }} src={formState.icon.url} alt="Icon" />
                </div>
              )}
            </div>
          </div>
        </div>
        {/* //PHKL Custom -e */}

        <div className={classes.rowContainer} style={{ marginBottom: 15 }}>
          <div className={classes.fieldContainer}>
            <span className={classes.field}>
              {Translation('newsCategory.common.color')}
              <span className={classes.mandatory}>*</span> :
            </span>
          </div>
          <MuiColorInput
            isAlphaHidden
            style={{ width: 200 }}
            name="color"
            margin="dense"
            variant="outlined"
            format="hex"
            error={errorState.mandatory.color}
            helperText={errorState.mandatory.color && Translation('field_mandatory_helper_label')}
            value={formState.color || ''}
            onChange={(color) => {
              onDismissErrorHandler('color', color);
              formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'color', value: color } });
            }}
          />
        </div>

        <div className={classes.rowContainer}>
          <div className={classes.fieldContainer}>
            <span className={classes.field}>
              {Translation('newsCategory.common.maxHeadLines')}
              <span className={classes.mandatory}>*</span> :
            </span>
          </div>
          <TextField
            style={{ width: 200 }}
            error={errorState.mandatory.maxNoOfHeadlines}
            margin="dense"
            variant="outlined"
            helperText={
              errorState.mandatory.maxNoOfHeadlines && Translation('newsCategory.error.mandatory.maxHeadLines')
            }
            inputProps={{
              className: classes.numberInput,
            }}
            type="number"
            value={formState.maxNoOfHeadlines}
            onChange={(e) => {
              onDismissErrorHandler('maxNoOfHeadlines', true);
              formDispatch({
                type: 'MODIFY_FIELD',
                payload: { field: 'maxNoOfHeadlines', value: e.target.value },
              });
            }}
          />
        </div>

        <div className={classes.rowContainer}>
          <div className={classes.fieldContainer}>
            <span className={classes.field}>
              {Translation('newsCategory.common.maxArchivedDays')}
              <span className={classes.mandatory}>*</span> :
            </span>
          </div>
          <TextField
            style={{ width: 200 }}
            error={errorState.mandatory.maxArchiveDays}
            margin="dense"
            variant="outlined"
            helperText={
              errorState.mandatory.maxArchiveDays && Translation('newsCategory.error.mandatory.maxArchivedDays')
            }
            inputProps={{
              className: classes.numberInput,
            }}
            type="number"
            value={formState.maxArchiveDays}
            onChange={(e) => {
              onDismissErrorHandler('maxArchiveDays', true);
              formDispatch({
                type: 'MODIFY_FIELD',
                payload: { field: 'maxArchiveDays', value: e.target.value },
              });
            }}
          />
        </div>
        <div className={classes.rowContainer}>
          <div className={classes.fieldContainer}>
            <span className={classes.field}>
              {Translation('component.formSelectItem.active')}
              <span className={classes.mandatory}>*</span> :
            </span>
          </div>
          <RadioGroup className={classes.flexRow}>
            <FormControlLabel
              value="Enable"
              control={<Radio disabled={false} />}
              label={Translation('app.checkbox.yes')}
              checked={formState.active === true}
              onChange={() => {
                formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'active', value: true } });
              }}
            />
            <FormControlLabel
              value="Disable"
              control={<Radio disabled={false} />}
              checked={formState.active === false}
              label={Translation('app.checkbox.no')}
              onChange={() => {
                formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'active', value: false } });
              }}
            />
          </RadioGroup>
        </div>

        <div className={classes.footerContainer}>
          <Button variant="contained" color="secondary" onClick={onSubmit}>
            {Translation('app.button.submit')}
          </Button>
        </div>
      </div>

      <SubmitAlertComponent
        open={!!confirmMsg}
        title={confirmMsg?.title}
        content={confirmMsg?.content}
        onCancel={onCancel}
        onOk={onOk}
      />

      {/** Error Dialog
      <Dialog
        onClose={() => {
          setErrorDialog((prev) => ({
            ...prev,
            show: false,
          }));
        }}
        open={errorDialog.show}
      >
        <div className={classes.container}>
          <div>{errorDialog.message}</div>
          <div className={classes.footer}>
            <Button
              variant="contained"
              color="inherit"
              onClick={() => {
                setErrorDialog((prev) => ({
                  ...prev,
                  show: false,
                }));
              }}
            >
              {Translation('global.text.okay')}
            </Button>
          </div>
        </div>
      </Dialog>
      */}
    </>
  );
};

export default NewsCategoryDetailForm;
